import { useEffect, useState } from "react";
import api from "../utils/api";
import { HiOutlineEye } from "react-icons/hi";
import Modal from "../components/Modal";
import ViewPayout from "../components/payout/ViewPayout";
import EditPayout from "../components/payout/EditPayout";
import EditBtn from "../components/EditBtn";
import DeleteBtn from "../components/DeleteBtn";

export default function PayoutList() {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState({ text: "", date: "" });
  const [textSearchInput, setTextSearchInput] = useState("");
  const [dateSearchInput, setDateSearchInput] = useState("");

  const itemsPerPage = 10;

  const [isModalOpenForEdit, setIsModalOpenForEdit] = useState(false);
  const [isModalOpenForView, setIsModalOpenForView] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);

  const fetchPayouts = async (page = 1, text = "", date = "") => {
    setLoading(true);
    try {
      const res = await api.get(`/payouts/user/list`, {
        params: { page, limit: itemsPerPage, text, date },
        withCredentials: true,
      });
      setPayouts(res.data.payouts || []);
      if (res.data.pagination) {
        setTotalPages(res.data.pagination.totalPages || 1);
      }
    } catch (err) {
      console.error("Error fetching payouts:", err);
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api
      .get("/users/me", { withCredentials: true })
      .then((res) => setUserRole(res.data.role))
      .catch(() => setUserRole(null));
  }, []);

  useEffect(() => {
    fetchPayouts(currentPage, search.text, search.date);
  }, [currentPage, search]);

  const deletePayout = async (id) => {
    if (!window.confirm("Are you sure you want to delete this payout?")) return;

    try {
      await api.delete(`/payouts/${id}`, { withCredentials: true });
      if (payouts.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchPayouts(currentPage, search);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to delete payout!");
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearch({ text: textSearchInput, date: dateSearchInput });
  };

  const handleClear = () => {
    setTextSearchInput("");
    setDateSearchInput("");
    setCurrentPage(1);
    setSearch({ text: "", date: "" });
  };

  return (
    <>
      <Modal
        isOpen={isModalOpenForView}
        onClose={() => {
          setIsModalOpenForView(false);
          setSelectedPayout(null);
        }}
      >
        {selectedPayout && <ViewPayout payout={selectedPayout} />}
      </Modal>

      <Modal
        isOpen={isModalOpenForEdit}
        onClose={() => setIsModalOpenForEdit(false)}
      >
        {selectedPayout && (
          <EditPayout
            payout={selectedPayout}
            onSuccess={async () => {
              setIsModalOpenForEdit(false);
              fetchPayouts(currentPage, search);
            }}
          />
        )}
      </Modal>

      <div>
        <h1 className="text-2xl font-bold mt-10 ml-2 mb-3">All Payouts</h1>

        <div className="ml-2 mt-5 bg-white rounded-md shadow-lg p-5">
          <div className="mb-4 flex justify-end gap-2">
            <input
              type="text"
              value={textSearchInput}
              onChange={(e) => setTextSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search by content owner or note..."
              className="w-96 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={dateSearchInput}
              onChange={(e) => setDateSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition"
            >
              Search
            </button>
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition"
            >
              Clear
            </button>
          </div>

          <table className="w-full text-sm border-separate border-spacing-y-2">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                <th className="px-4 py-3 text-left rounded-tl-xl rounded-bl-xl">
                  Content Owner
                </th>
                <th className="px-4 py-3 text-center">Paid At</th>
                <th className="px-4 py-3 text-center">Amount</th>
                <th className="px-4 py-3 text-center">Payment Method</th>
                {userRole && ["Admin", "Manager"].includes(userRole) ? (
                  <>
                    <th className="px-4 py-3 text-center">Note</th>
                    <th className="px-4 py-3 text-right rounded-tr-xl rounded-br-xl">
                      Actions
                    </th>
                  </>
                ) : (
                  <th className="px-4 py-3 text-center rounded-tr-xl rounded-br-xl">
                    Note
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    Loading payouts...
                  </td>
                </tr>
              ) : payouts.length ? (
                payouts.map((payout) => (
                  <tr
                    key={payout._id}
                    className="border-t transition hover:bg-slate-100"
                  >
                    <td className="px-4 py-4 font-medium">
                      {payout.contentOwnerId?.name || "N/A"}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {new Date(payout.paidAt).toLocaleDateString("en-GB")}
                    </td>
                    <td className="px-4 py-4 text-center font-semibold">
                      ${payout.amount}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {payout.paymentMethod}
                    </td>
                    <td className="px-4 py-4 text-center text-gray-600">
                      {payout.note || "-"}
                    </td>
                    {userRole && ["Admin", "Manager"].includes(userRole) && (
                      <td className="px-4 py-4 flex justify-end gap-4">
                        <button
                          onClick={() => {
                            setSelectedPayout(payout);
                            setIsModalOpenForView(true);
                          }}
                        >
                          <HiOutlineEye className="h-5 w-5 transform hover:scale-110 text-green-400 hover:text-green-700 transition" />
                        </button>
                        <EditBtn
                          onClick={() => {
                            setSelectedPayout(payout);
                            setIsModalOpenForEdit(true);
                          }}
                          label="Edit Payout"
                        />
                        <DeleteBtn
                          onClick={() => deletePayout(payout._id)}
                          label="Delete Payout"
                        />
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    No payouts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-1 mt-4">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>

              <span className="px-3 py-1 font-medium">
                {currentPage} / {totalPages}
              </span>

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
